import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

export const Badge = ({ children, className, testId }) => (
  <span data-testid={testId} className={cn("inline-flex items-center gap-1.5 rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-3.5 py-1.5 text-xs font-semibold tracking-wide text-[#92400E]", className)}>
    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
    {children}
  </span>
);

const base = "inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-[transform,background-color,box-shadow] duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0";
const styles = {
  primary: "bg-cta text-navy shadow-md hover:bg-cta-hover hover:shadow-lg",
  secondary: "border border-ocean bg-white text-ocean hover:bg-[#F0F7FC]",
};

export const Btn = ({ to, href, variant = "primary", arrow = true, children, className, testId, ...props }) => {
  const cls = cn(base, styles[variant], className);
  const inner = (
    <>
      {children}
      {arrow && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
    </>
  );
  if (to) return <Link to={to} className={cls} data-testid={testId} {...props}>{inner}</Link>;
  if (href) return <a href={href} className={cls} data-testid={testId} target="_blank" rel="noopener noreferrer" {...props}>{inner}</a>;
  return <button className={cls} data-testid={testId} {...props}>{inner}</button>;
};

export const Reveal = ({ children, delay = 0, className, y = 24 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({ badge, title, highlight, subtitle, center = false, light = false }) => (
  <Reveal className={cn("mb-12 max-w-3xl sm:mb-16", center && "mx-auto text-center")}>
    {badge && <Badge className="mb-5">{badge}</Badge>}
    <h2 className={cn("text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-[2.75rem]", light && "text-white")}>
      {title} {highlight && <span className={light ? "text-cta" : "text-ocean"}>{highlight}</span>}
    </h2>
    {subtitle && <p className={cn("mt-4 text-base md:text-lg", light ? "text-white/75" : "text-body")}>{subtitle}</p>}
  </Reveal>
);

export const PageHero = ({ badge, title, highlight, subtitle, children }) => (
  <section className="relative overflow-hidden border-b border-line bg-white">
    <div className="dot-grid absolute inset-0 opacity-60" aria-hidden="true" />
    <div className="container-x relative py-16 sm:py-20 lg:py-24">
      <Reveal className="max-w-3xl">
        {badge && <Badge className="mb-5">{badge}</Badge>}
        <h1 className="text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
          {title} {highlight && <span className="text-ocean">{highlight}</span>}
        </h1>
        {subtitle && <p className="mt-5 text-base text-body md:text-lg">{subtitle}</p>}
        {children}
      </Reveal>
    </div>
  </section>
);

export const Spinner = ({ className }) => (
  <div className={cn("flex items-center justify-center py-24", className)} data-testid="loading-spinner">
    <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-sky/30 border-t-ocean" />
  </div>
);
